import React, { createContext, useCallback, useContext, useState } from "react";

interface FooterConfig {
  onSave: () => void;
  loading?: boolean;
  disabled?: boolean;
}

type RegisterFooterFn = (config: FooterConfig | null) => void;

interface SetupFooterContextValue {
  footerConfig: FooterConfig | null;
  registerFooter: RegisterFooterFn;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const SetupFooterContext = createContext<SetupFooterContextValue>({
  footerConfig: null,
  registerFooter: noop,
});

export const SetupFooterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [footerConfig, setFooterConfig] = useState<FooterConfig | null>(null);

  const registerFooter = useCallback((config: FooterConfig | null) => {
    setFooterConfig(config);
  }, []);

  return (
    <SetupFooterContext.Provider value={{ footerConfig, registerFooter }}>
      {children}
    </SetupFooterContext.Provider>
  );
};

export const useSetupFooter = (config: FooterConfig | null) => {
  const { registerFooter } = useContext(SetupFooterContext);
  const configRef = React.useRef(config);
  configRef.current = config;

  React.useEffect(() => {
    if (!configRef.current) {
      registerFooter(null);
      return;
    }
    registerFooter({
      ...configRef.current,
      onSave: () => configRef.current?.onSave(),
    });
    return () => registerFooter(null);
  }, [config?.loading, config?.disabled, registerFooter]);
};

export const useSetupFooterConfig = () => {
  return useContext(SetupFooterContext).footerConfig;
};
